import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { DASHBOARD } from "@/constants/testIds";
import Modal from "@/components/Modal";

/**
 * Sprint 5.4 — Bulk CSV importer for the Master Catalog.
 * Flow: download template → pick file + mode → validate (no writes) → confirm import.
 * The user can fix the CSV and re-validate as many times as needed before committing.
 */
export default function CSVImportModal({ open, onClose, onImported }) {
  const { t } = useI18n();
  const [file, setFile] = useState(null);
  const [matchBy, setMatchBy] = useState("sku");
  const [preview, setPreview] = useState(null); // { rows, summary }
  const [validating, setValidating] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setMatchBy("sku");
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/financial-products/import/template`, {
        withCredentials: true,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "master_catalog_template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const handleValidate = async () => {
    if (!file) {
      toast.error(t("csv_import_no_file"));
      return;
    }
    setValidating(true);
    setPreview(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("match_by", matchBy);
      const res = await axios.post(
        `${API}/financial-products/import/preview`,
        fd,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      setPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setValidating(false);
    }
  };

  const handleExecute = async () => {
    if (!file || !preview) return;
    if (preview.summary.errors > 0) {
      toast.error(t("csv_import_has_errors"));
      return;
    }
    setExecuting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("match_by", matchBy);
      const res = await axios.post(`${API}/financial-products/import`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      toast.success(t("csv_import_success"));
      if (onImported) onImported();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setExecuting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={t("csv_import_title")}
      testId={DASHBOARD.productsImportModal}
      wide
    >
      <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
        <p className="text-sm text-[var(--text-secondary)]">{t("csv_import_subtitle")}</p>

        {/* Step 1 — Download template */}
        <div className="border border-[var(--border)] bg-[var(--surface-alt)] p-3">
          <div className="label-overline mb-2">{t("csv_import_step_1")}</div>
          <button
            type="button"
            data-testid={DASHBOARD.productsImportTemplateBtn}
            onClick={downloadTemplate}
            className="bg-black text-white px-4 py-2 font-display font-bold text-sm hover:bg-[var(--accent-cyan,#0E5FFF)] transition-colors"
          >
            ↓ {t("csv_import_template_btn")}
          </button>
          <div className="text-xs text-[var(--text-secondary)] mt-2">{t("csv_import_template_hint")}</div>
        </div>

        {/* Step 2 — Matching mode */}
        <div className="border border-[var(--border)] bg-[var(--surface-alt)] p-3">
          <div className="label-overline mb-2">{t("csv_import_step_2")}</div>
          <div className="space-y-1.5" data-testid={DASHBOARD.productsImportMatchBy}>
            {[
              { v: "sku", k: "csv_import_match_sku" },
              { v: "name", k: "csv_import_match_name" },
              { v: "create_only", k: "csv_import_match_create_only" },
            ].map((opt) => (
              <label
                key={opt.v}
                className="flex items-center gap-2 cursor-pointer text-sm font-display"
              >
                <input
                  type="radio"
                  name="match_by"
                  value={opt.v}
                  checked={matchBy === opt.v}
                  onChange={() => setMatchBy(opt.v)}
                  className="w-4 h-4"
                />
                {t(opt.k)}
              </label>
            ))}
          </div>
        </div>

        {/* Step 3 — Upload + validate */}
        <div className="border border-[var(--border)] bg-[var(--surface-alt)] p-3">
          <div className="label-overline mb-2">{t("csv_import_step_3")}</div>
          <input
            type="file"
            accept=".csv,text/csv"
            data-testid={DASHBOARD.productsImportFileInput}
            onChange={(e) => {
              setFile(e.target.files?.[0] || null);
              setPreview(null);
              setResult(null);
            }}
            className="w-full font-display text-sm border border-[var(--border)] px-2 py-1.5 bg-white"
          />
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              data-testid={DASHBOARD.productsImportPreviewBtn}
              onClick={handleValidate}
              disabled={!file || validating}
              className="bg-[var(--accent-cyan,#0E5FFF)] text-white px-4 py-2 font-display font-bold text-sm disabled:opacity-50"
            >
              {validating ? t("csv_import_validating") : t("csv_import_validate_btn")}
            </button>
          </div>
        </div>

        {/* Preview output */}
        {preview ? (
          <div className="border border-[var(--border)] bg-white p-3 space-y-3">
            <div
              data-testid={DASHBOARD.productsImportSummary}
              className="grid grid-cols-4 gap-2 text-center"
            >
              <SummaryCell label={t("csv_import_summary_rows")} value={preview.summary.rows} tone="neutral" />
              <SummaryCell label={t("csv_import_summary_to_create")} value={preview.summary.to_create} tone="ok" />
              <SummaryCell label={t("csv_import_summary_to_update")} value={preview.summary.to_update} tone="info" />
              <SummaryCell label={t("csv_import_summary_errors")} value={preview.summary.errors} tone={preview.summary.errors ? "err" : "ok"} />
            </div>

            <div className={`text-xs font-display ${preview.summary.errors ? "text-red-600" : "text-green-700"}`}>
              {preview.summary.errors ? t("csv_import_has_errors") : t("csv_import_no_errors")}
            </div>

            {/* Per-row results — show all errors, plus first ~50 ok rows */}
            <div className="max-h-64 overflow-y-auto border-t border-[var(--border)]">
              <table className="w-full text-xs font-mono-tech">
                <thead className="sticky top-0 bg-[var(--bg)]">
                  <tr className="text-left">
                    <th className="p-1.5 w-12">#</th>
                    <th className="p-1.5 w-24">Acción</th>
                    <th className="p-1.5">Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.rows
                    .slice() // copy
                    .sort((a, b) => {
                      // Errors first
                      const aErr = a.action === "error" ? 0 : 1;
                      const bErr = b.action === "error" ? 0 : 1;
                      if (aErr !== bErr) return aErr - bErr;
                      return a.row_number - b.row_number;
                    })
                    .slice(0, 100)
                    .map((r) => (
                      <tr
                        key={r.row_number}
                        data-testid={DASHBOARD.productsImportRow(r.row_number)}
                        className="border-t border-[var(--border)] align-top"
                      >
                        <td className="p-1.5 font-bold">{r.row_number}</td>
                        <td className="p-1.5">
                          <ActionBadge action={r.action} t={t} />
                        </td>
                        <td className="p-1.5">
                          <div className="font-display font-bold">{r.resolved?.name || "—"}</div>
                          {r.errors?.length ? (
                            <div className="text-red-700 mt-0.5 whitespace-pre-wrap">
                              {r.errors.join(" · ")}
                            </div>
                          ) : null}
                          {r.warnings?.length ? (
                            <div className="text-amber-700 mt-0.5">⚠ {r.warnings.join(" · ")}</div>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              {preview.rows.length > 100 ? (
                <div className="text-xs text-[var(--text-secondary)] p-2">
                  + {preview.rows.length - 100} más filas (no mostradas)
                </div>
              ) : null}
            </div>

            {!result ? (
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  data-testid={DASHBOARD.productsImportExecuteBtn}
                  onClick={handleExecute}
                  disabled={executing || preview.summary.errors > 0 || (preview.summary.to_create + preview.summary.to_update === 0)}
                  className="bg-black text-white px-4 py-2 font-display font-bold text-sm disabled:opacity-40"
                >
                  {executing ? t("csv_import_executing") : t("csv_import_execute_btn")}
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        {/* Result block */}
        {result ? (
          <div className="border-2 border-black bg-white p-3">
            <div className="label-overline mb-2">✓ {t("csv_import_success")}</div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <SummaryCell label={t("csv_import_result_created")} value={result.created} tone="ok" />
              <SummaryCell label={t("csv_import_result_updated")} value={result.updated} tone="info" />
              <SummaryCell label={t("csv_import_result_skipped")} value={result.skipped} tone={result.skipped ? "err" : "neutral"} />
            </div>
          </div>
        ) : null}

        <div className="flex justify-end pt-2">
          <button
            type="button"
            data-testid={DASHBOARD.productsImportCancelBtn}
            onClick={handleClose}
            className="border border-black px-4 py-2 font-display font-bold text-sm hover:bg-black hover:text-white transition-colors"
          >
            {t("csv_import_close")}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function SummaryCell({ label, value, tone }) {
  const toneClass = {
    ok: "bg-green-50 border-green-600 text-green-800",
    err: "bg-red-50 border-red-600 text-red-800",
    info: "bg-blue-50 border-blue-600 text-blue-800",
    neutral: "bg-[var(--bg)] border-[var(--border)] text-black",
  }[tone || "neutral"];
  return (
    <div className={`border ${toneClass} p-2`}>
      <div className="text-[10px] font-mono-tech tracking-widest uppercase opacity-80">{label}</div>
      <div className="font-display font-bold text-2xl tabular-nums">{value}</div>
    </div>
  );
}

function ActionBadge({ action, t }) {
  const map = {
    create: { label: t("csv_import_action_create"), cls: "bg-green-600 text-white" },
    update: { label: t("csv_import_action_update"), cls: "bg-blue-600 text-white" },
    error: { label: t("csv_import_action_error"), cls: "bg-red-600 text-white" },
  };
  const c = map[action] || map.error;
  return (
    <span className={`${c.cls} px-1.5 py-0.5 font-mono-tech text-[10px] font-bold tracking-wider`}>
      {c.label}
    </span>
  );
}
